import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Quote, ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { TESTIMONIALS } from '../config/siteContent';

/**
 * TestimonialsCarousel - light theme.
 * Auto-advances every 5s, pauses when the user clicks prev/next/dot.
 */
const TestimonialsCarousel = () => {
  const [active, setActive] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);

  useEffect(() => {
    if (!autoPlay) return;
    const interval = setInterval(() => {
      setActive((a) => (a + 1) % TESTIMONIALS.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [autoPlay]);

  const next = () => {
    setAutoPlay(false);
    setActive((a) => (a + 1) % TESTIMONIALS.length);
  };
  const prev = () => {
    setAutoPlay(false);
    setActive((a) => (a - 1 + TESTIMONIALS.length) % TESTIMONIALS.length);
  };

  const t = TESTIMONIALS[active];

  return (
    <section
      id="testimonials"
      className="relative py-24 lg:py-32 bg-bg-soft overflow-hidden"
    >
      {/* Soft sky glow */}
      <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
        <div className="w-[800px] h-[400px] rounded-full bg-accent-soft/40 blur-3xl" />
      </div>

      <div className="relative max-w-6xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 max-w-3xl xl:max-w-none mx-auto"
        >
          <p className="eyebrow">Customer Stories</p>
          <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-text-primary mb-4 tracking-[-0.02em] text-balance xl:whitespace-nowrap">
            <span className="text-gradient-sky">Trusted</span>{' '}
            by industry leaders
          </h2>
        </motion.div>

        <div className="relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={t.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              className="card-elevated p-8 sm:p-12 lg:p-16 text-center mx-4 lg:mx-0"
            >
              <Quote
                size={44}
                className="text-accent-primary/45 mx-auto mb-6"
                strokeWidth={1.5}
              />

              {/* Stars */}
              <div className="flex justify-center gap-1 mb-6">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    size={16}
                    className="fill-accent-primary text-accent-primary"
                  />
                ))}
              </div>

              <p className="font-display font-medium text-xl sm:text-2xl lg:text-3xl text-text-primary leading-relaxed mb-8 text-balance">
                "{t.quote}"
              </p>

              <div className="flex items-center justify-center gap-4">
                <div className="w-14 h-14 rounded-full bg-gradient-sky shadow-glow-sky flex items-center justify-center text-white font-display font-bold text-base">
                  {t.initials}
                </div>
                <div className="text-left">
                  <div className="font-semibold text-text-primary">{t.name}</div>
                  <div className="text-sm text-text-muted">
                    {t.title},{' '}
                    <span className="text-accent-primary font-medium">
                      {t.company}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Prev/next */}
          <button
            onClick={prev}
            className="absolute left-0 lg:-left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white border border-border-subtle shadow-soft flex items-center justify-center text-text-secondary hover:text-accent-primary hover:border-accent-primary/40 transition-all"
            aria-label="Previous testimonial"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            onClick={next}
            className="absolute right-0 lg:-right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white border border-border-subtle shadow-soft flex items-center justify-center text-text-secondary hover:text-accent-primary hover:border-accent-primary/40 transition-all"
            aria-label="Next testimonial"
          >
            <ChevronRight size={18} />
          </button>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-2 mt-8">
          {TESTIMONIALS.map((_, i) => (
            <button
              key={i}
              onClick={() => {
                setActive(i);
                setAutoPlay(false);
              }}
              aria-label={`Go to testimonial ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${
                i === active
                  ? 'w-8 bg-accent-primary'
                  : 'w-1.5 bg-border-subtle hover:bg-text-muted'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsCarousel;
